import React from 'react';
import _ from 'lodash';
import { ParticipantPermission } from 'livekit-server-sdk';
import { LocalParticipant, Participant, RemoteParticipant, Room, RoomEvent, TrackPublication } from 'livekit-client';

import { selectIsBroadcasting } from 'client/redux/mux-details/selectors';
import { selectUserUID } from 'client/redux/client/selectors';
import { selectRoomStatus } from 'client/redux/room/selectors';
import { useAppDispatch, useAppSelector } from 'client/hooks';
import { setIsShowJoinToSpacePopup } from 'client/redux/ui';
import { updateActiveSpeakers } from 'client/redux/pubnub';
import { eventBus } from 'client/utils/event-bus';
import { ROOM_STATUS } from 'constants/index';

import { Context, useContextReducer } from './context';

type Props = React.PropsWithChildren<unknown>;

export const LiveStreamConnector: React.FC<Props> = ({ children }) => {
  const appDispatch = useAppDispatch();
  const roomStatus = useAppSelector(selectRoomStatus);
  const userUid = useAppSelector(selectUserUID);
  const [state, dispatch] = useContextReducer();
  const isBroadcasting = useAppSelector(selectIsBroadcasting);
  const spaceRef = React.useRef<Room | null>(null);
  const isPlayingCounterRef = React.useRef<number>(0);
  const userUidRef = React.useRef<string>(userUid);

  userUidRef.current = userUid;

  const handleActiveSpeakerChangeEvent = (data: Participant[]) => {
    if (roomStatus !== ROOM_STATUS.ACTIVE) return;

    const message = _.map(data, (item) => item.identity);

    appDispatch(updateActiveSpeakers(message));
  };

  const addListeners = () => {
    if (spaceRef.current) {
      spaceRef.current.addListener(RoomEvent.AudioPlaybackStatusChanged, handleAudioPlaybackStatusChanged);
      spaceRef.current.addListener(RoomEvent.ParticipantPermissionsChanged, _.debounce(handlePermissionChanged, 100));
      spaceRef.current.addListener(RoomEvent.TrackMuted, handleToggleTrackMuteState);
      spaceRef.current.addListener(RoomEvent.TrackUnmuted, handleToggleTrackMuteState);

      spaceRef.current.addListener(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakerChangeEvent);
      spaceRef.current.addListener(RoomEvent.ParticipantConnected, addParticipant);
      spaceRef.current.addListener(RoomEvent.ParticipantDisconnected, removeParticipant);
    }
  };

  const removeAllListeners = () => {
    if (spaceRef.current) {
      spaceRef.current.removeAllListeners(RoomEvent.AudioPlaybackStatusChanged);
      spaceRef.current.removeAllListeners(RoomEvent.ParticipantPermissionsChanged);
      spaceRef.current.removeAllListeners(RoomEvent.TrackMuted);
      spaceRef.current.removeAllListeners(RoomEvent.TrackUnmuted);

      spaceRef.current.removeAllListeners(RoomEvent.ActiveSpeakersChanged);
      spaceRef.current.removeAllListeners(RoomEvent.ParticipantConnected);
      spaceRef.current.removeAllListeners(RoomEvent.ParticipantDisconnected);
    }
  };

  const initSpace = async () => {
    if (spaceRef.current) return;

    console.info('init space');

    const space = new Room();

    spaceRef.current = space;

    addListeners();

    dispatch({ type: 'SET_SPACE', payload: space });
  };

  const destroySpace = () => {
    if (spaceRef.current) {
      console.info('destroy space');
      removeAllListeners();
      spaceRef.current.disconnect();

      spaceRef.current = null;
      dispatch({ type: 'SET_SPACE', payload: null });
      dispatch({ type: 'SET_LOCAL_PARTICIPANT', payload: null });
    }
  };

  const handleAudioPlaybackStatusChanged = (playing: boolean) => {
    console.info('handleAudioPlaybackStatusChanged', playing);
    if (!playing && isPlayingCounterRef.current < 1) {
      spaceRef.current
        ?.startAudio()
        .then(() => console.log('audio started'))
        .catch((error) => {
          console.error(error);

          console.info(error.name);
          console.info(error.message);

          if (error.name === 'NotAllowedError' && error.message.includes('user denied permission')) {
            dispatch({ type: 'SET_START_AUDIO_ISSUE', payload: true });
          }
        });
      isPlayingCounterRef.current++;
    }
  };

  const handleToggleTrackMuteState = (publication: TrackPublication, participant: Participant) => {
    if (participant.identity === userUidRef.current) {
      eventBus.publish('TOGGLE_LOCAL_MIC', { isMuted: publication.track?.isMuted ?? true });
    }
  };

  const handlePermissionChanged = (
    prevPermissions: ParticipantPermission | undefined,
    participant: LocalParticipant | RemoteParticipant,
  ) => {
    if (userUidRef.current === participant.identity) {
      if (participant.permissions?.canPublish) {
        appDispatch(setIsShowJoinToSpacePopup(true));
      } else {
        spaceRef.current?.localParticipant.setMicrophoneEnabled(false);
      }
    } else {
      if (participant.permissions?.canPublish) {
        dispatch({ type: 'ADD_PARTICIPANT', payload: participant as RemoteParticipant });
      } else {
        dispatch({ type: 'REMOVE_PARTICIPANT', payload: participant as RemoteParticipant });
      }
    }
  };

  const addParticipant = React.useCallback(
    (participant: RemoteParticipant) => {
      if (participant.permissions?.canPublish) {
        console.info('participant, who can publish audio tracks, joined');
        dispatch({ type: 'ADD_PARTICIPANT', payload: participant });
      }
    },
    [dispatch],
  );

  const removeParticipant = React.useCallback(
    (participantLeaving: RemoteParticipant) => {
      console.info('participant removed');
      dispatch({ type: 'REMOVE_PARTICIPANT', payload: participantLeaving });
    },
    [dispatch],
  );

  // catch option when broadcasting is started/finished
  React.useEffect(
    function handleIsBroadcastingChange() {
      if (roomStatus === ROOM_STATUS.ACTIVE && isBroadcasting) {
        console.info('broadcasting is started');
        initSpace();
      } else if (roomStatus === ROOM_STATUS.FINISHED && !isBroadcasting && spaceRef.current) {
        console.info('broadcasting is finished');
        destroySpace();
      }
    },
    [isBroadcasting, roomStatus],
  );

  // catch option when user login or logout during the stream
  React.useEffect(
    function handleUserAuthStatusChange() {
      if (!spaceRef.current) return;

      spaceRef.current.disconnect();

      dispatch({ type: 'SET_LOCAL_PARTICIPANT', payload: null });
    },
    [userUid],
  );

  return <Context.Provider value={{ state, dispatch }}>{children}</Context.Provider>;
};
